/**
 * Who moves on a plate, and how exactly (people, or any object):
 *
 *   ┌ the plate, each one found outlined and numbered ─────┐ ┌ tools ──────────────┐
 *   │  click one: it moves / stays still                     │ │ Pick · Add · Moves · │
 *   │  paint: add a person or object, or mend an outline     │ │ Stays still · Erase  │
 *   │  (or: the background behind them · the motion, live)   │ │ the list, Undo …    │
 *
 * The background behind whatever moves is filled in by the server (so
 * nothing looks cut out); when the brush makes more move than was filled,
 * it says so, and fills it on request. Brush marks apply as the brush lifts.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLockPageScroll } from '../../hooks/useLockPageScroll';
import { createPortal } from 'react-dom';
import {
  FiX, FiLoader, FiCheck, FiAlertTriangle, FiRotateCcw, FiPlus, FiTrash2, FiMousePointer, FiEdit2, FiSlash, FiDelete, FiEye, FiFilm, FiImage, FiSearch,
} from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { showError } from '../FullStory/shared';
import { useNow, since } from './plateTime';
import { PlateLive } from './PlateLive';

const W = 960;
const H = 540;
const K = W / 1920; // design pixels → editor pixels
const TOOLS = [['pick', FiMousePointer], ['add', FiPlus], ['moves', FiEdit2], ['still', FiSlash], ['erase', FiDelete]];
// Each one's colour on the plate (moves); grey when it stays still.
const HUES = ['#22C55E', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#EF4444', '#84CC16', '#06B6D4', '#F97316'];

const blank = () => Object.assign(document.createElement('canvas'), { width: W, height: H });
// Fetched as a file, then drawn: a picture from the server's address straight into
// a canvas would "taint" it, and its pixels (the outlines, the brush marks) couldn't be read.
const loadImage = async (src) => {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return createImageBitmap(await res.blob());
};

/** The brush's two layers (what moves, what stays) as the server takes them: grey, 255 moves, 128 stays. */
function paintPng(moves, still) {
  const m = moves.getContext('2d').getImageData(0, 0, W, H).data;
  const s = still.getContext('2d').getImageData(0, 0, W, H).data;
  const out = blank();
  const ctx = out.getContext('2d');
  const img = ctx.createImageData(W, H);
  let any = false;
  for (let i = 0; i < W * H; i++) {
    const v = m[i * 4 + 3] > 127 ? 255 : s[i * 4 + 3] > 127 ? 128 : 0;
    if (v) any = true;
    img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return any ? out.toDataURL('image/png') : null;
}

/** Marks over a person or object to add: white on black. */
function marksPng(marks) {
  const a = marks.getContext('2d').getImageData(0, 0, W, H).data;
  const out = blank();
  const ctx = out.getContext('2d');
  const img = ctx.createImageData(W, H);
  let any = false;
  for (let i = 0; i < W * H; i++) {
    const v = a[i * 4 + 3] > 40 ? 255 : 0;
    if (v) any = true;
    img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return any ? out.toDataURL('image/png') : null;
}

export const PeopleEditor = ({ plate, look, onClose, onChanged }) => {
  useLockPageScroll();
  const t = useT();
  const [data, setData] = useState(null);
  const [tool, setTool] = useState('pick');
  const [size, setSize] = useState(28);
  const [view, setView] = useState('plate'); // plate | behind | motion
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState('');
  const [marked, setMarked] = useState(false);
  const [mattes, setMattes] = useState({}); // id → { canvas (tinted), alpha (ImageData), x, y, w, h }
  const overlay = useRef(null);
  const layers = useRef({ moves: blank(), still: blank(), marks: blank() });
  const history = useRef([]);
  const drawing = useRef(null);
  const working = data?.task?.status === 'working';
  const now = useNow(working);

  const load = useCallback(async () => {
    try { setData(await api.platePeople(plate.id)); } catch (e) { showError(e); }
  }, [plate.id]);
  useEffect(() => { load(); }, [load]);

  // While a job runs (adding one, filling the background): ask now and then.
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(async () => {
      try {
        const next = await api.platePeople(plate.id);
        setData(next);
        if (!next.task) { onChanged?.(); layers.current.marks = blank(); setMarked(false); setLabel(''); }
      } catch { /* the next try */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [working, plate.id, onChanged]);

  // Each one's outline, tinted (and kept to know which one a click is on).
  const subjectsKey = JSON.stringify((data?.subjects || []).map((s) => [s.id, s.x, s.y, s.w, s.h]));
  useEffect(() => {
    if (!data?.subjects?.length) return undefined;
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(data.subjects.map(async (s, k) => {
        try {
          const img = await loadImage(api.plateTemplateUrl(plate.id, `subject-${s.id}.png`, s.id));
          const w = Math.max(1, Math.round(s.w * K)); const h = Math.max(1, Math.round(s.h * K));
          const c = Object.assign(document.createElement('canvas'), { width: w, height: h });
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const grey = ctx.getImageData(0, 0, w, h);
          const tinted = ctx.createImageData(w, h);
          const hue = HUES[k % HUES.length];
          const [r, g, b] = [1, 3, 5].map((j) => parseInt(hue.slice(j, j + 2), 16));
          for (let i = 0; i < w * h; i++) {
            tinted.data[i * 4] = r; tinted.data[i * 4 + 1] = g; tinted.data[i * 4 + 2] = b; tinted.data[i * 4 + 3] = grey.data[i * 4];
          }
          ctx.putImageData(tinted, 0, 0);
          // Where its number goes: the top of the outline itself (its box leaves room around it).
          let top = { x: w / 2, y: 0 };
          for (let y = 0, found = false; y < h && !found; y++) {
            let x0 = -1; let x1 = -1;
            for (let x = 0; x < w; x++) if (grey.data[(y * w + x) * 4] > 128) { if (x0 < 0) x0 = x; x1 = x; }
            if (x0 >= 0) { top = { x: (x0 + x1) / 2, y }; found = true; }
          }
          out[s.id] = { canvas: c, alpha: grey, x: s.x * K, y: s.y * K, w, h, hue, top };
        } catch { /* drawn without it */ }
      }));
      if (!cancelled) setMattes(out);
    })();
    return () => { cancelled = true; };
  }, [plate.id, subjectsKey]); // eslint-disable-line

  // The brush marks saved before: back into the two layers.
  useEffect(() => {
    if (!data?.painted) return;
    (async () => {
      try {
        const img = await loadImage(`${api.plateTemplateUrl(plate.id, 'people-paint.png', `p${data.rev}`)}`);
        const c = blank(); const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, W, H);
        const px = ctx.getImageData(0, 0, W, H).data;
        const moves = blank(); const still = blank();
        const mi = moves.getContext('2d').createImageData(W, H); const si = still.getContext('2d').createImageData(W, H);
        for (let i = 0; i < W * H; i++) {
          const v = px[i * 4];
          if (v > 190) { mi.data[i * 4 + 1] = 200; mi.data[i * 4 + 3] = 255; } else if (v > 60) { si.data[i * 4] = 230; si.data[i * 4 + 3] = 255; }
        }
        moves.getContext('2d').putImageData(mi, 0, 0); still.getContext('2d').putImageData(si, 0, 0);
        layers.current.moves = moves; layers.current.still = still;
        redraw();
      } catch { /* none */ }
    })();
  }, [data?.painted, plate.id]); // eslint-disable-line

  /** The overlay: outlines (moving: coloured; still: grey), the brush's marks, marks to add. */
  const redraw = useCallback(() => {
    const c = overlay.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    if (view === 'plate') {
      (data?.subjects || []).forEach((s) => {
        const m = mattes[s.id];
        if (!m) return;
        ctx.globalAlpha = s.selected ? 0.45 : 0.18;
        if (!s.selected) ctx.filter = 'grayscale(1)';
        ctx.drawImage(m.canvas, m.x, m.y, m.w, m.h);
        ctx.filter = 'none';
      });
      ctx.globalAlpha = 0.55;
      ctx.drawImage(layers.current.moves, 0, 0);
      ctx.drawImage(layers.current.still, 0, 0);
    }
    ctx.globalAlpha = 0.6;
    ctx.drawImage(layers.current.marks, 0, 0);
    ctx.globalAlpha = 1;
    // Numbers by each one.
    if (view === 'plate') {
      (data?.subjects || []).forEach((s, k) => {
        const m = mattes[s.id];
        const x = (m ? m.x + m.top.x : s.x * K) - 10; const y = (m ? m.y + m.top.y : s.y * K) + 4;
        ctx.fillStyle = s.selected ? (mattes[s.id]?.hue || '#22C55E') : '#6B7280';
        ctx.beginPath(); ctx.arc(x + 10, y + 10, 11, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(String(k + 1), x + 10, y + 11);
      });
    }
  }, [data, mattes, view]);
  useEffect(() => { redraw(); }, [redraw]);

  const at = (e) => {
    const r = overlay.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };

  const save = useCallback(async (change) => {
    setBusy('saving');
    try {
      setData(await api.updatePlatePeople(plate.id, change));
      onChanged?.();
    } catch (e) { showError(e); } finally { setBusy(''); }
  }, [plate.id, onChanged]);

  /** A click with "Pick": the one under it moves, or stays still. */
  const pick = (p) => {
    const hits = (data?.subjects || []).filter((s) => {
      const m = mattes[s.id];
      if (!m) return false;
      const x = Math.floor(p.x - m.x); const y = Math.floor(p.y - m.y);
      return x >= 0 && y >= 0 && x < m.w && y < m.h && m.alpha.data[(y * m.w + x) * 4] > 100;
    });
    if (!hits.length) return;
    // The smallest one there (one in front of a larger one).
    const s = hits.sort((a, b) => a.w * a.h - b.w * b.h)[0];
    toggle(s.id);
  };
  const toggle = (id) => {
    const selected = data.subjects.filter((s) => (s.id === id ? !s.selected : s.selected)).map((s) => s.id);
    setData((d) => ({ ...d, subjects: d.subjects.map((s) => ({ ...s, selected: selected.includes(s.id) })) }));
    save({ selected });
  };

  const snapshot = () => {
    const copy = (c) => { const n = blank(); n.getContext('2d').drawImage(c, 0, 0); return n; };
    history.current.push({ moves: copy(layers.current.moves), still: copy(layers.current.still), marks: copy(layers.current.marks) });
    if (history.current.length > 30) history.current.shift();
  };
  const stroke = (from, to) => {
    const target = tool === 'add' ? 'marks' : tool === 'still' ? 'still' : 'moves';
    const paint = (layer, colour, erase = false) => {
      const ctx = layers.current[layer].getContext('2d');
      ctx.globalCompositeOperation = erase ? 'destination-out' : 'source-over';
      ctx.strokeStyle = colour; ctx.lineWidth = size; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
    };
    if (tool === 'erase') { paint('moves', '#000', true); paint('still', '#000', true); paint('marks', '#000', true); } else {
      paint(target, target === 'marks' ? '#FDE047' : target === 'still' ? '#E11D48' : '#16A34A');
      // A part is either moving or still, not both.
      if (target === 'moves') paint('still', '#000', true);
      if (target === 'still') paint('moves', '#000', true);
    }
    redraw();
  };
  const down = (e) => {
    if (view === 'motion' || working) return;
    const p = at(e);
    if (tool === 'pick') { pick(p); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    snapshot();
    drawing.current = p;
    stroke(p, p);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = at(e);
    stroke(drawing.current, p);
    drawing.current = p;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = null;
    if (tool === 'add') { setMarked(Boolean(marksPng(layers.current.marks))); return; }
    if (tool !== 'erase' || !marksPng(layers.current.marks)) save({ paint: paintPng(layers.current.moves, layers.current.still) });
    else setMarked(Boolean(marksPng(layers.current.marks)));
  };
  const undo = useCallback(() => {
    const last = history.current.pop();
    if (!last) return;
    layers.current = last;
    redraw();
    setMarked(Boolean(marksPng(last.marks)));
    save({ paint: paintPng(last.moves, last.still) });
  }, [redraw, save]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
      if (e.key === 'Escape') onClose();
      if (e.key === '[') setSize((v) => Math.max(4, v - 4));
      if (e.key === ']') setSize((v) => Math.min(140, v + 4));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, onClose]);

  const clearBrush = () => {
    snapshot();
    layers.current.moves = blank(); layers.current.still = blank();
    redraw();
    save({ paint: null });
  };
  const addOne = async () => {
    const area = marksPng(layers.current.marks);
    if (!area) return;
    setBusy('adding');
    try { setData(await api.addPlatePeopleSubject(plate.id, { area, label })); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const fill = async () => {
    setBusy('filling');
    try { setData(await api.fillPlatePeople(plate.id)); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const removeOne = async (id) => {
    try { setData(await api.removePlatePeopleSubject(plate.id, id)); onChanged?.(); } catch (e) { showError(e); }
  };
  const dismiss = async () => {
    try { setData(await api.clearPlatePeopleTask(plate.id)); } catch (e) { showError(e); }
  };

  const source = view === 'behind'
    ? api.plateTemplateUrl(plate.id, 'people-clean.jpg', `c${data?.rev || 0}`)
    : api.plateTemplateUrl(plate.id, 'source.jpg', plate.updatedAt);
  const cursor = tool === 'pick' ? 'pointer' : 'crosshair';

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-gray-50 flex flex-col" role="dialog" aria-modal="true" aria-label={t('plateTemplates.people.editor.title')}>
      <header className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-gray-900">{t('plateTemplates.people.editor.title')}</h2>
          <p className="text-[11px] text-gray-500">{t('plateTemplates.people.editor.hint')}</p>
        </div>
        {busy === 'saving' && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.people.editor.saving')}</span>}
        <button type="button" onClick={onClose} className="btn btn-primary btn-sm flex items-center gap-1"><FiCheck size={14} /> {t('plateTemplates.people.editor.done')}</button>
        <button type="button" onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm px-2"><FiX size={18} /></button>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <main className="flex-1 min-w-0 overflow-y-auto p-4">
          <div className="max-w-5xl mx-auto space-y-2">
            <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-gray-200/70 text-xs font-medium">
              {[['plate', FiImage], ['behind', FiEye], ['motion', FiFilm]].map(([v, Icon]) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Icon size={13} /> {t(`plateTemplates.people.editor.view.${v}`)}
                </button>
              ))}
            </div>
            <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-sm select-none">
              {view === 'motion' ? (
                <PlateLive plateId={plate.id} words={false} look={{ people: { ...(look || {}), enabled: true } }} version={`${plate.updatedAt}|${data?.rev || 0}`} />
              ) : (
                <>
                  <img src={source} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
                  <canvas
                    ref={overlay} width={W} height={H}
                    onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
                    className="absolute inset-0 w-full h-full touch-none" style={{ cursor }}
                  />
                </>
              )}
              {working && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="inline-flex items-center gap-2 rounded-full bg-black/75 text-white text-xs px-3 py-1.5">
                    <FiLoader size={13} className="animate-spin" /> {t(`plateTemplates.people.editor.task.${data.task.kind}`)} <span className="tabular-nums text-white/70">{since(data.task.startedAt, now)}</span>
                  </span>
                </div>
              )}
            </div>
            <p className="text-[11px] text-gray-500">{t(view === 'behind' ? 'plateTemplates.people.editor.behindHint' : view === 'motion' ? 'plateTemplates.people.editor.motionHint' : `plateTemplates.people.editor.toolHint.${tool}`)}</p>
            {data?.uncovered && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <FiAlertTriangle size={14} /> {t('plateTemplates.people.editor.uncovered')}
                <button type="button" onClick={fill} disabled={working || busy === 'filling'} className="btn btn-primary btn-sm text-xs ml-auto">{t('plateTemplates.people.editor.fill')}</button>
              </div>
            )}
            {data?.task?.status === 'failed' && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <FiAlertTriangle size={14} /> {data.task.error}
                <button type="button" onClick={dismiss} className="underline ml-auto">{t('plateTemplates.close')}</button>
              </div>
            )}
          </div>
        </main>

        <aside className="lg:w-[360px] flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto p-4 space-y-5">
          {!data ? <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.live.loading')}</p> : (
            <>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.tools')}</h3>
                <div className="grid grid-cols-5 gap-1">
                  {TOOLS.map(([id, Icon]) => (
                    <button key={id} type="button" onClick={() => { setTool(id); if (view === 'motion') setView('plate'); }} aria-pressed={tool === id} title={t(`plateTemplates.people.editor.tool.${id}`)} className={`flex flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5 text-[10px] ${tool === id ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                      <Icon size={15} /> {t(`plateTemplates.people.editor.tool.${id}`)}
                    </button>
                  ))}
                </div>
                {tool !== 'pick' && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    {t('plateTemplates.people.editor.size')}
                    <input type="range" min={4} max={140} value={size} onChange={(e) => setSize(Number(e.target.value))} className="flex-1 accent-primary-600" />
                    <span className="w-8 tabular-nums text-right">{size}</span>
                  </label>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={undo} disabled={!history.current.length} className="btn btn-ghost btn-sm text-xs flex items-center gap-1"><FiRotateCcw size={12} /> {t('plateTemplates.people.editor.undo')}</button>
                  {data.painted && <button type="button" onClick={clearBrush} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.people.editor.clearBrush')}</button>}
                </div>
              </section>

              {tool === 'add' && (
                <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.addTitle')}</h3>
                  <p className="text-[11px] text-gray-600">{t('plateTemplates.people.editor.addHint')}</p>
                  <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder={t('plateTemplates.people.editor.labelPlaceholder')} className="input text-sm" />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={addOne} disabled={!marked || working || busy === 'adding'} className="btn btn-primary btn-sm flex items-center gap-1">
                      {busy === 'adding' ? <FiLoader size={13} className="animate-spin" /> : <FiSearch size={13} />} {t('plateTemplates.people.editor.addGo')}
                    </button>
                    {marked && <button type="button" onClick={() => { snapshot(); layers.current.marks = blank(); setMarked(false); redraw(); }} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.people.editor.clearMarks')}</button>}
                  </div>
                  <p className="text-[10px] text-gray-500">{t('plateTemplates.people.editor.addCost')}</p>
                </section>
              )}

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.list', { moving: data.subjects.filter((s) => s.selected).length, count: data.subjects.length })}</h3>
                <ul className="space-y-1">
                  {data.subjects.map((s, k) => (
                    <li key={s.id} className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
                      <input type="checkbox" checked={s.selected} onChange={() => toggle(s.id)} disabled={working} className="rounded border-gray-300" aria-label={t('plateTemplates.people.editor.moves', { n: k + 1 })} />
                      <span className="w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center" style={{ background: s.selected ? HUES[k % HUES.length] : '#9CA3AF' }}>{k + 1}</span>
                      <span className="flex-1 text-xs text-gray-700 truncate">
                        {s.label || t(s.kind === 'added' ? 'plateTemplates.people.editor.object' : 'plateTemplates.people.editor.person', { n: k + 1 })}
                        {s.rough && <span className="ml-1 text-amber-700">· {t('plateTemplates.people.editor.rough')}</span>}
                      </span>
                      <span className="text-[10px] text-gray-500">{t(s.selected ? 'plateTemplates.people.editor.moving' : 'plateTemplates.people.editor.still')}</span>
                      {data.subjects.length > 1 && <button type="button" onClick={() => removeOne(s.id)} disabled={working} title={t('plateTemplates.remove')} aria-label={t('plateTemplates.remove')} className="text-gray-400 hover:text-red-600 px-1"><FiTrash2 size={12} /></button>}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-gray-500">{t('plateTemplates.people.editor.listHint')}</p>
              </section>
            </>
          )}
        </aside>
      </div>
    </div>,
    document.body,
  );
};

export default PeopleEditor;
